import { useEffect, useEffectEvent, useId, useLayoutEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Icon } from "./Icon";
import { t } from "../../i18n";

type Props = {
  open: boolean;
  onClose: () => void;
  /** Sheet title (text-title-2, left-aligned). Also names the dialog. */
  title?: ReactNode;
  /** Secondary line under the title (text-subhead, secondary colour). */
  subtitle?: ReactNode;
  children: ReactNode;
  /** Actions pinned under the content, on the sheet background, with safe-area padding. */
  footer?: ReactNode;
  /** "auto" fits the content (up to the top safe area); "full" is always tall. */
  size?: "auto" | "full";
  /** Dim the content behind (default true). The backdrop still closes the sheet on tap. */
  dim?: boolean;
  testId?: string;
};

const CLOSE_MS = 480; // --dur-sheet
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Keep in sync with the desktop breakpoint in styles.css (sheets are dialogs from there). */
const DESKTOP = window.matchMedia("(min-width: 1024px)");

type Drag ={ id: number; y0: number; h: number; samples: { t: number; y: number }[] };

// The page behind never scrolls while a sheet (or another full-screen layer) is open. Counted: sheets
// can hand over to each other, and open over the search screen.
let scrollLocks = 0;
export function lockScroll() {
  if (scrollLocks++ === 0) document.documentElement.style.overflow = "hidden";
}
export function unlockScroll() {
  if (--scrollLocks === 0) document.documentElement.style.overflow = "";
}

/**
 * Bottom sheet. Opens and closes with the iOS sheet curve; drag the grabber or header down to
 * dismiss (velocity aware). Focus moves into the sheet and returns to the opener on close.
 * Content scrolls with contained overscroll, so it never drags the page or the map.
 */
export function Sheet({ open, onClose, title, subtitle, children, footer, size = "auto", dim = true, testId }: Props) {
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(false);
  const panel = useRef<HTMLDivElement>(null);
  const backdrop = useRef<HTMLDivElement>(null);
  const drag = useRef<Drag | null>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const titleId = useId();

  // Mount, then slide in on the next frame; slide out, then unmount.
  useEffect(() => {
    if (open) {
      returnFocus.current ??= document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setMounted(true);
      let inner = 0;
      const outer = requestAnimationFrame(() => (inner = requestAnimationFrame(() => setShown(true))));
      return () => {
        cancelAnimationFrame(outer);
        cancelAnimationFrame(inner);
      };
    }
    setShown(false);
    const tm = setTimeout(() => setMounted(false), CLOSE_MS);
    return () => clearTimeout(tm);
  }, [open]);

  // A re-open while still closing starts from a clean state (a drag-dismiss leaves inline styles).
  useLayoutEffect(() => {
    if (!open) return;
    for (const el of [panel.current, backdrop.current]) el?.removeAttribute("style");
  }, [open]);

  // Focus: into the sheet on open (unless a field inside already took it), back to the opener on close.
  useEffect(() => {
    if (!open || !mounted) return;
    const p = panel.current;
    if (p && !p.contains(document.activeElement)) p.focus({ preventScroll: true });
  }, [open, mounted]);
  useEffect(() => {
    if (open) return;
    const el = returnFocus.current;
    returnFocus.current = null;
    if (el?.isConnected) el.focus({ preventScroll: true });
  }, [open]);

  const onEscape = useEffectEvent((e: KeyboardEvent) => e.key === "Escape" && onClose());
  useEffect(() => {
    if (!open) return;
    lockScroll();
    window.addEventListener("keydown", onEscape);
    return () => {
      window.removeEventListener("keydown", onEscape);
      unlockScroll();
    };
  }, [open]);

  if (!mounted) return null;

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    // A dialog on desktop widths: nothing to drag (it closes with ×, Escape or a click outside).
    if (DESKTOP.matches) return;
    if ((e.target as HTMLElement).closest("button,a,input,select,textarea,[data-no-drag]")) return;
    const p = panel.current;
    if (!p) return;
    drag.current = { id: e.pointerId, y0: e.clientY, h: p.offsetHeight, samples: [{ t: performance.now(), y: e.clientY }] };
    e.currentTarget.setPointerCapture(e.pointerId);
    p.style.transition = "none";
    if (backdrop.current) backdrop.current.style.transition = "none";
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    const p = panel.current;
    if (!d || !p || e.pointerId !== d.id) return;
    const now = performance.now();
    d.samples.push({ t: now, y: e.clientY });
    while (d.samples.length > 2 && now - d.samples[0]!.t > 100) d.samples.shift(); // velocity window: last 100ms
    const dy = e.clientY - d.y0;
    p.style.transform = `translate3d(0, ${dy >= 0 ? dy : -2 * Math.sqrt(-dy)}px, 0)`; // resists upward drags
    if (backdrop.current) backdrop.current.style.opacity = String(1 - Math.min(1, Math.max(0, dy) / d.h));
  };
  const onPointerEnd = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    const p = panel.current;
    if (!d || !p || e.pointerId !== d.id) return;
    drag.current = null;
    const first = d.samples[0]!;
    const last = d.samples[d.samples.length - 1]!;
    const dy = (e.type === "pointercancel" ? last.y : e.clientY) - d.y0;
    // px/ms over the window (at least one frame); none when the finger paused before lifting.
    const v = performance.now() - last.t > 90 ? 0 : (last.y - first.y) / Math.max(16, last.t - first.t);
    p.style.transition = "";
    if (backdrop.current) backdrop.current.style.transition = "";
    if (dy > d.h * 0.3 || (v > 0.5 && dy > 12)) {
      // Keep going from where the finger left it.
      p.style.transform = "translate3d(0, 100%, 0)";
      if (backdrop.current) backdrop.current.style.opacity = "0";
      onClose();
    } else {
      p.style.transform = "";
      if (backdrop.current) backdrop.current.style.opacity = "";
    }
  };

  // Keep keyboard focus inside the modal sheet.
  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const p = panel.current;
    if (e.key !== "Tab" || !p) return;
    const items = [...p.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.getClientRects().length > 0);
    if (!items.length) return e.preventDefault();
    const first = items[0]!;
    const last = items[items.length - 1]!;
    if (e.shiftKey && (document.activeElement === first || document.activeElement === p)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div className="sheet-root" data-shown={shown} data-testid={testId}>
      <div ref={backdrop} className="sheet-backdrop" data-dim={dim} onClick={onClose} aria-hidden />
      <div
        ref={panel}
        className="sheet-panel"
        data-size={size}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <div className="sheet-drag" onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerEnd} onPointerCancel={onPointerEnd}>
          <div className="sheet-grabber" aria-hidden />
          <div className="flex items-start gap-3 px-gutter pb-3 pt-2.5">
            <div className="min-w-0 flex-1">
              {title ? (
                <div id={titleId} role="heading" aria-level={2} className="text-title-2">
                  {title}
                </div>
              ) : null}
              {subtitle ? <div className="mt-0.5 text-subhead text-label-2">{subtitle}</div> : null}
            </div>
            <button type="button" onClick={onClose} className="sheet-close" aria-label={t("common.close")}>
              <span>
                <Icon name="close" size={16} weight="bold" />
              </span>
            </button>
          </div>
        </div>
        <div className="sheet-body">{children}</div>
        {footer ? <div className="sheet-footer">{footer}</div> : <div className="shrink-0" style={{ height: "var(--safe-bottom)" }} />}
      </div>
    </div>,
    document.body,
  );
}
